import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import qs from 'qs'
import { Grid, Link as MuiLink, Tooltip, Typography } from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { useCurrentUser } from '@utils/useCurrentUser'
import { sendEmproveDownloadsEvent } from '@utils/analytics'
import {
  SnackbarMessages,
  useGlobalSnackbar,
} from '@src/components/GlobalSnackbar/globalSnackBarContext'
import { Actions, documentAccessType, getBigQueryData } from './utils'
import PurchaseModal from './PurchaseModal'
import EmailVerificationModal from '@src/components/EmailVerificationModal'
import DownloadModal from '@src/components/EmproveDownloadModal'
import { useCurrentUserLazyQuery } from '@src/queries/CurrentUserQuery.generated'
import { usePostBigQueryDataMutation } from '@src/mutations/PostBigQueryData.generated'

const useStyles = makeStyles((theme: Theme) => {
  return {
    availableDocument: {
      cursor: 'pointer',
      fontSize: theme.typography.body1.fontSize,
    },
    availableScanNowDocument: {
      cursor: 'pointer',
      fontSize: theme.typography.pxToRem(14),
      paddingBottom: theme.spacing(4),
      '&:last-of-type': {
        paddingBottom: 0,
      },
    },
    disabledDocument: {
      color: theme.palette.grey[100],
      cursor: 'default',
    },
    scanNowDisabledDocument: {
      color: theme.palette.grey[100],
      cursor: 'default',
      paddingBottom: theme.spacing(4),
      '&:first-of-type': {
        paddingTop: theme.spacing(4),
      },
      '&:last-of-type': {
        paddingBottom: 0,
      },
    },
    documentIcon: {
      display: 'inline-flex',
      paddingRight: theme.spacing(2),
      '& > svg': {
        fontSize: theme.typography.body1.fontSize,
      },
    },
    documentDetails: {
      paddingLeft: theme.spacing(5),
    },
    documentTitle: {
      alignItems: 'center',
    },
    iconText: {
      display: 'flex',
    },
  }
})

export const DownloadLink = ({
  classes,
  document,
  details,
  postBigQueryDataMutation,
  userDataEmprove,
  currentUser,
  isScanNow = false,
}) => {
  const { setSnackbar } = useGlobalSnackbar()
  const downloadUrl = `/emprove/download/${document?.assetId}?${qs.stringify({
    dossierFileName: document?.fileName,
    dossierTypeCode: document?.type,
    dossierRelatedProduct: document?.productName,
    dossierRelatedProductNo: document?.product,
    dossierOrigin: isScanNow ? 'ScanNow' : 'PDP',
  })}`
  const className = isScanNow
    ? classes.availableScanNowDocument
    : classes.availableDocument

  const page = isScanNow ? 'ScanNow' : 'PDP'

  return (
    <a
      className={className}
      href={downloadUrl}
      onClick={() => {
        sendEmproveDownloadsEvent(document, downloadUrl, document.productNumber)
        try {
          postBigQueryDataMutation({
            variables: {
              input: getBigQueryData(
                currentUser,
                document,
                userDataEmprove,
                page
              ),
            },
          })
        } finally {
          setSnackbar(SnackbarMessages.DownloadWillBeginShortly)
        }
      }}
      download
    >
      {!isScanNow && (
        <span className={classes.documentIcon}>
          <details.Icon />
        </span>
      )}
      <span className={document.displayType}>{document.displayType}</span>
    </a>
  )
}

const DocumentDetails = ({
  document,
  userData,
  setShouldRedirect,
  productName,
  emailSent,
  setEmailSent,
  isScanNow = false,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()

  const {
    currentUser,
    userIsLoggedIn,
    userIsFullyAuthenticated,
    isPublicOrderingCustomer,
  } = useCurrentUser({
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })

  // Variables
  const details = documentAccessType(
    document,
    userData?.getEmproveUserDataV2,
    userIsLoggedIn,
    isPublicOrderingCustomer
  )

  const [isDownload, setIsDownload] = useState(false)

  // Modal states
  const [purchaseModalOpen, setPurchaseModalOpen] = useState(false)
  const [emailVerificationModalOpen, setEmailVerificationModalOpen] =
    useState(false)
  const [downloadModalOpen, setDownloadModalOpen] = useState(false)
  const [postBigQueryDataMutation] = usePostBigQueryDataMutation()

  // LazyQuery for modal close
  const [getCurrentUser, { data: currentUserData }] = useCurrentUserLazyQuery({
    fetchPolicy: 'network-only',
  })

  // LazyQuery for modal open
  const [getCurrentUserWithCallback, { data: currentUserWithCallbackData }] =
    useCurrentUserLazyQuery({
      fetchPolicy: 'network-only',
      onCompleted: () => {
        if (!userIsFullyAuthenticated) {
          setShouldRedirect(true)
        } else if (
          currentUser?.__typename === 'LoggedInUser' &&
          !currentUser?.participantCommunicationSetting
            .emproveEmailVerification &&
          (details.action === Actions.Purchase ||
            details.action === Actions.Download)
        ) {
          setEmailVerificationModalOpen(true)
        } else if (details.action === Actions.Purchase) {
          setPurchaseModalOpen(true)
        } else if (details.action === Actions.Download) {
          setDownloadModalOpen(true)
        }
      },
    })

  useEffect(() => {
    setIsDownload(
      userIsFullyAuthenticated &&
        currentUser?.__typename === 'LoggedInUser' &&
        currentUser?.participantCommunicationSetting.emproveTermsConditions &&
        currentUser?.participantCommunicationSetting.emproveEmailVerification &&
        details?.action === Actions.Download
    )
  }, [currentUser, currentUserData, currentUserWithCallbackData])

  const handleClick = (event) => {
    event.preventDefault()
    getCurrentUserWithCallback()
  }

  if (!details) {
    return null
  }

  return details.disabled ? (
    <Tooltip
      title={details.message ? formatMessage(details.message) : ''}
      placement="bottom"
    >
      <Grid
        container
        className={
          isScanNow ? classes.scanNowDisabledDocument : classes.disabledDocument
        }
      >
        <Grid item className={classes.documentIcon}>
          <details.Icon />
        </Grid>
        {document.displayType}
      </Grid>
    </Tooltip>
  ) : (
    <>
      {isDownload ? (
        <DownloadLink
          classes={classes}
          document={document}
          details={details}
          userDataEmprove={userData?.getEmproveUserDataV2}
          currentUser={currentUser}
          postBigQueryDataMutation={postBigQueryDataMutation}
          isScanNow={isScanNow}
        />
      ) : isScanNow ? (
        <MuiLink
          color="primary"
          onClick={handleClick}
          className={classes.availableScanNowDocument}
        >
          {document.displayType}
        </MuiLink>
      ) : (
        <MuiLink
          color="primary"
          onClick={handleClick}
          className={classes.availableDocument}
        >
          <Grid container className={classes.documentTitle}>
            <div className={classes.iconText}>
              <Grid item className={classes.documentIcon}>
                <details.Icon />
              </Grid>
              <Grid item>{document.displayType}</Grid>
            </div>
          </Grid>
        </MuiLink>
      )}
      {!isScanNow && details.message && (
        <Typography
          variant="body2"
          component="p"
          className={classes.documentDetails}
        >
          {formatMessage(details.message)}
        </Typography>
      )}
      {emailVerificationModalOpen && (
        <EmailVerificationModal
          open={emailVerificationModalOpen}
          setModalOpen={(boolean) => {
            setEmailVerificationModalOpen(boolean)
            getCurrentUser()
          }}
          setEmailSent={setEmailSent}
          emailSent={emailSent}
        />
      )}
      {purchaseModalOpen && (
        <PurchaseModal
          open={purchaseModalOpen}
          setModalOpen={(boolean) => {
            setPurchaseModalOpen(boolean)
            getCurrentUser()
          }}
          document={document}
        />
      )}
      {downloadModalOpen && (
        <DownloadModal
          open={downloadModalOpen}
          closeModal={() => {
            setDownloadModalOpen(false)
            getCurrentUser()
          }}
          document={document}
          productName={productName}
        />
      )}
    </>
  )
}

export default DocumentDetails
